import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { stories as mockStories } from '../../mocks/homeData';
import { fetchDynamicById, fetchPublicDynamics, incrementDynamicView } from '../../api/dripPlan';

interface StoryDetail {
  id: number;
  title: string;
  category: string;
  image: string;
  excerpt: string;
  content: string;
  date: string;
  views: number;
  author: string;
  tags: string[];
}

// 故事详情页专用的工作场景图片
const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 600'%3E%3Crect fill='%23e5e7eb' width='1200' height='600'/%3E%3C/svg%3E";
const storyDetailImages: Record<number, string> = {
  1: PLACEHOLDER_IMG,
  2: PLACEHOLDER_IMG,
  3: PLACEHOLDER_IMG,
  4: PLACEHOLDER_IMG,
};

const StoryDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [story, setStory] = useState<StoryDetail | null>(null);
  const [relatedStories, setRelatedStories] = useState<typeof mockStories>([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [showShareCertModal, setShowShareCertModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({ name: '', content: '' });
  const [messageSubmitted, setMessageSubmitted] = useState(false);
  const [showCopySuccessModal, setShowCopySuccessModal] = useState(false);
  const [showWechatSuccessModal, setShowWechatSuccessModal] = useState(false);

  useEffect(() => {
    const storyId = parseInt(id || '0', 10);
    if (!storyId) return;

    let cancelled = false;

    (async () => {
      const apiDetail = await fetchDynamicById(storyId);
      if (cancelled) return;
      if (apiDetail) {
        const content = apiDetail.content || '';
        const contentHtml = content.trim().startsWith('<') ? content : content.split('\n').map((p) => `<p>${p}</p>`).join('');
        const detailStory: StoryDetail = {
          id: apiDetail.id,
          title: apiDetail.title,
          category: apiDetail.category,
          image: apiDetail.image,
          excerpt: apiDetail.excerpt,
          content: contentHtml,
          date: apiDetail.date,
          views: apiDetail.views,
          author: apiDetail.author || '点滴公益编辑部',
          tags: apiDetail.tags || [apiDetail.category].filter(Boolean),
        };
        setStory(detailStory);
        setLikeCount(0);
        incrementDynamicView(storyId).catch(() => {});
        const list = await fetchPublicDynamics();
        if (!cancelled && list) {
          const related = list.filter((s) => s.id !== storyId && s.category === apiDetail.category).slice(0, 3);
          setRelatedStories(related);
        }
        return;
      }

      const foundStory = mockStories.find((s) => s.id === storyId);
      if (foundStory) {
        const detailStory: StoryDetail = {
          ...foundStory,
          image: storyDetailImages[storyId] || foundStory.image,
          content: `
          <p>清晨5点，当城市还在沉睡中，环卫工人李师傅已经开始了一天的工作。他推着清扫车，在空旷的街道上默默清理着昨夜留下的垃圾。</p>
          <p>李师傅今年52岁，在南岸区从事环卫工作已经15年了。"这份工作虽然辛苦，但看到街道变得干净整洁，心里就很满足。"他笑着说道。</p>
          <h3>夏日的坚守</h3>
          <p>重庆的夏天格外炎热，地表温度常常超过40度。李师傅和同事们每天要在烈日下工作8个小时以上。</p>
          <h3>点滴公益的温暖</h3>
          <p>去年夏天，李师傅第一次接触到"点滴公益"。志愿者们送来了清凉的矿泉水，还有防暑降温的药品。</p>
          <h3>传递爱心</h3>
          <p>受到帮助的李师傅，也开始用自己的方式回馈社会。点滴公益让我明白，每个人的一点点付出，汇聚起来就能成为温暖的力量。</p>
        `,
          author: '点滴公益编辑部',
          tags: ['环卫工人', '夏日送水', '城市守护者', '爱心传递'],
        };
        setStory(detailStory);
        setLikeCount(Math.floor(Math.random() * 500) + 100);
        const related = mockStories.filter((s) => s.id !== storyId && s.category === foundStory.category).slice(0, 3);
        setRelatedStories(related);
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = story?.title || '';
    const shareText = `我正在阅读"点滴公益"的一线故事《${title}》，为城市守护者点赞！`;
    
    switch(platform) {
      case 'wechat':
        // 跳转到微信朋友圈分享
        window.open(`weixin://dl/moments`);
        // 显示分享成功弹窗
        setTimeout(() => {
          setShowWechatSuccessModal(true);
        }, 500);
        break;
      case 'copy':
        navigator.clipboard.writeText(`${shareText} ${url}`);
        setShowCopySuccessModal(true);
        break;
    }
    setShowShareMenu(false);
  };

  const handleShareCert = (platform: string) => {
    const shareText = `我正在关注"点滴公益"的一线故事《${story?.title}》，为城市守护者点赞！快来一起传递温暖！`;
    const shareUrl = window.location.href;
    
    switch(platform) {
      case 'wechat':
        window.open(`weixin://dl/moments`);
        setTimeout(() => {
          setShowWechatSuccessModal(true);
        }, 500);
        break;
      case 'copy':
        navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setShowCopySuccessModal(true);
        break;
    }
    setShowShareCertModal(false);
  };

  const handleMessageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessageSubmitted(true);
    setTimeout(() => {
      setShowMessageModal(false);
      setMessageSubmitted(false);
      setMessageForm({ name: '', content: '' });
    }, 2000);
  };

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      setLikeCount(prev => prev + 1);
    } else {
      setLiked(false);
      setLikeCount(prev => prev - 1);
    }
  };

  if (!story) {
    return (
      <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-search-line text-6xl text-[#6C6C6C] mb-4"></i>
          <p className="text-xl text-[#6C6C6C]">故事不存在</p>
          <Link to="/" className="inline-block mt-6 px-6 py-3 bg-[#2A7A62] text-white rounded-lg hover:bg-[#236854] transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <Navbar />

      {/* 面包屑导航 */}
      <div className="bg-white border-b border-[#E0E0E0] pt-16 md:pt-20">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-[#6C6C6C]">
            <Link to="/" className="hover:text-[#2A7A62] transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/#stories" className="hover:text-[#2A7A62] transition-colors cursor-pointer">一线故事</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#1E1E1E] truncate max-w-[120px] md:max-w-none">{story.title}</span>
          </div>
        </div>
      </div>

      {/* 文章头部 */}
      <article className="py-6 md:py-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          {/* 标题区域 */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-12 mb-4 md:mb-8">
            {/* 移动端：标签横排三个一排 */}
            <div className="flex flex-wrap gap-1.5 md:gap-3 mb-4 md:mb-6">
              <span className="px-2 py-1 md:px-4 md:py-2 bg-[#F4B942] text-white text-[10px] md:text-sm font-medium rounded-full">
                {story.category}
              </span>
              {story.tags.slice(0, 3).map((tag, index) => (
                <span key={index} className="px-2 py-1 md:px-3 md:py-1 bg-[#E8F4F0] text-[#2A7A62] text-[10px] md:text-sm rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="text-xl md:text-5xl font-bold text-[#1E1E1E] mb-3 md:mb-6 leading-tight">
              {story.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 md:gap-6 text-[#6C6C6C] text-xs md:text-sm pb-4 md:pb-6 border-b border-[#E0E0E0]">
              <div className="flex items-center gap-1 md:gap-2">
                <i className="ri-user-line text-sm md:text-lg"></i>
                <span>{story.author}</span>
              </div>
              <div className="flex items-center gap-1 md:gap-2">
                <i className="ri-calendar-line text-sm md:text-lg"></i>
                <span>{story.date}</span>
              </div>
              <div className="flex items-center gap-1 md:gap-2">
                <i className="ri-eye-line text-sm md:text-lg"></i>
                <span>{story.views.toLocaleString()} 阅读</span>
              </div>
            </div>

            {/* 分享按钮 */}
            <div className="flex items-center gap-2 md:gap-4 mt-4 md:mt-6">
              <span className="text-xs md:text-sm text-[#6C6C6C]">分享到：</span>
              <div className="relative">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="flex items-center gap-1 md:gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#E8F4F0] text-[#2A7A62] rounded-lg hover:bg-[#2A7A62] hover:text-white transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm"
                >
                  <i className="ri-share-line"></i>
                  <span>分享</span>
                </button>

                {showShareMenu && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-[#E0E0E0] py-2 z-10 min-w-[140px] md:min-w-[160px]">
                    <button
                      onClick={() => handleShare('wechat')}
                      className="w-full px-3 md:px-4 py-2 text-left hover:bg-[#F9F9F9] flex items-center gap-2 md:gap-3 cursor-pointer whitespace-nowrap text-xs md:text-sm"
                    >
                      <i className="ri-wechat-line text-[#09BB07] text-lg md:text-xl"></i>
                      <span>微信朋友圈</span>
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-full px-3 md:px-4 py-2 text-left hover:bg-[#F9F9F9] flex items-center gap-2 md:gap-3 cursor-pointer whitespace-nowrap text-xs md:text-sm"
                    >
                      <i className="ri-link text-[#6C6C6C] text-lg md:text-xl"></i>
                      <span>复制链接</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 封面图片 - 移动端更小 */}
          <div className="mb-4 md:mb-8 rounded-xl md:rounded-2xl overflow-hidden shadow-lg">
            <img
              src={story.image}
              alt={story.title}
              className="w-full h-[200px] md:h-[500px] object-cover"
            />
          </div>

          {/* 文章内容 - 移动端字体更小 */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-12 mb-4 md:mb-8">
            <div 
              className="prose prose-sm md:prose-lg max-w-none"
              style={{
                lineHeight: '1.8',
                color: '#1E1E1E'
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: story.content }} 
                className="text-sm md:text-lg [&>p]:mb-4 md:[&>p]:mb-6 [&>h3]:text-lg md:[&>h3]:text-2xl [&>h3]:font-bold [&>h3]:text-[#2A7A62] [&>h3]:mt-6 md:[&>h3]:mt-8 [&>h3]:mb-3 md:[&>h3]:mb-4"
              />
            </div>

            {/* 文章底部操作 - 移动端按钮一排显示 */}
            <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-[#E0E0E0] flex items-center justify-between gap-2 md:gap-0">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center justify-center gap-1 md:gap-2 px-3 py-2 md:px-6 md:py-3 bg-[#E8F4F0] text-[#2A7A62] rounded-lg hover:bg-[#2A7A62] hover:text-white transition-colors cursor-pointer whitespace-nowrap text-xs md:text-base"
              >
                <i className="ri-arrow-left-line text-sm md:text-base"></i>
                <span>返回上一步</span>
              </button>

              <button 
                onClick={handleLike}
                className={`flex items-center justify-center gap-1 md:gap-2 px-3 py-2 md:px-6 md:py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap text-xs md:text-base ${
                  liked 
                    ? 'bg-[#E06B3D] text-white' 
                    : 'bg-[#2A7A62] text-white hover:bg-[#236854]'
                }`}
              >
                <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-sm md:text-base`}></i>
                <span>{liked ? '已点赞' : '点赞支持'}</span>
                <span className="ml-1">({likeCount})</span>
              </button>
            </div>

            {/* 更多操作按钮 */}
            <div className="mt-4 md:mt-6 flex flex-wrap gap-2 md:gap-3">
              <button
                onClick={() => setShowShareCertModal(true)}
                className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2 bg-[#F9F9F9] text-[#6C6C6C] rounded-lg hover:bg-[#E8F4F0] hover:text-[#2A7A62] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm"
              >
                <i className="ri-share-forward-line"></i>
                <span>分享证书</span>
              </button>
              <button
                onClick={() => setShowMessageModal(true)}
                className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2 bg-[#F9F9F9] text-[#6C6C6C] rounded-lg hover:bg-[#E8F4F0] hover:text-[#2A7A62] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm"
              >
                <i className="ri-message-3-line"></i>
                <span>提交留言</span>
              </button>
            </div>
          </div>

          {/* 相关故事推荐 */}
          {relatedStories.length > 0 && (
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-12">
              <h2 className="text-lg md:text-3xl font-bold text-[#2A7A62] mb-4 md:mb-8 flex items-center gap-2 md:gap-3">
                <i className="ri-file-list-3-line"></i>
                相关故事推荐
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
                {relatedStories.map((relatedStory) => (
                  <Link
                    key={relatedStory.id}
                    to={`/story/${relatedStory.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="bg-[#F9F9F9] rounded-lg md:rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300">
                      <div className="relative h-[100px] md:h-[200px] overflow-hidden">
                        <img
                          src={relatedStory.image}
                          alt={relatedStory.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 right-2 md:top-3 md:right-3 px-2 py-0.5 md:px-3 md:py-1 bg-[#F4B942] text-white text-[9px] md:text-xs font-medium rounded-full">
                          {relatedStory.category}
                        </div>
                      </div>
                      <div className="p-2 md:p-4">
                        <h3 className="text-xs md:text-lg font-bold text-[#1E1E1E] mb-1 md:mb-2 line-clamp-2 group-hover:text-[#2A7A62] transition-colors">
                          {relatedStory.title}
                        </h3>
                        <p className="text-[10px] md:text-sm text-[#6C6C6C] line-clamp-2 mb-2 md:mb-3 hidden md:block">
                          {relatedStory.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-[9px] md:text-xs text-[#6C6C6C]">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {relatedStory.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {relatedStory.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* 分享证书弹窗 */}
      {showShareCertModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowShareCertModal(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1E1E1E]">分享证书</h3>
              <button onClick={() => setShowShareCertModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F9F9] cursor-pointer">
                <i className="ri-close-line text-xl text-[#6C6C6C]"></i>
              </button>
            </div>
            
            {/* 证书预览 */}
            <div className="bg-gradient-to-br from-[#E8F4F0] to-[#F9F9F9] rounded-xl p-4 mb-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#2A7A62] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-heart-fill text-2xl text-white"></i>
                </div>
                <p className="text-sm text-[#6C6C6C] mb-1">我正在关注</p>
                <p className="text-base font-bold text-[#1E1E1E] mb-2 line-clamp-2">{story?.title}</p>
                <p className="text-xs text-[#2A7A62]">点滴公益 · 传递温暖</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <button onClick={() => handleShareCert('wechat')} className="w-full flex items-center gap-4 p-4 bg-[#F9F9F9] rounded-xl hover:bg-[#E8F4F0] transition-colors cursor-pointer">
                <div className="w-10 h-10 bg-[#07C160] rounded-full flex items-center justify-center">
                  <i className="ri-wechat-fill text-xl text-white"></i>
                </div>
                <span className="text-base font-medium text-[#1E1E1E]">分享到微信朋友圈</span>
              </button>
              <button onClick={() => handleShareCert('copy')} className="w-full flex items-center gap-4 p-4 bg-[#F9F9F9] rounded-xl hover:bg-[#E8F4F0] transition-colors cursor-pointer">
                <div className="w-10 h-10 bg-[#6C6C6C] rounded-full flex items-center justify-center">
                  <i className="ri-link text-xl text-white"></i>
                </div>
                <span className="text-base font-medium text-[#1E1E1E]">复制链接</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 提交留言弹窗 */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowMessageModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1E1E1E]">提交留言</h3>
              <button onClick={() => setShowMessageModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F9F9] cursor-pointer">
                <i className="ri-close-line text-xl text-[#6C6C6C]"></i>
              </button>
            </div>
            
            {messageSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#2A7A62] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-white"></i>
                </div>
                <h4 className="text-xl font-bold text-[#1E1E1E] mb-2">留言已提交</h4>
                <p className="text-[#6C6C6C]">感谢您的支持与鼓励！</p>
              </div>
            ) : (
              <form onSubmit={handleMessageSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1E1E1E] mb-2">您的称呼</label>
                  <input
                    type="text"
                    required
                    value={messageForm.name}
                    onChange={(e) => setMessageForm({...messageForm, name: e.target.value})}
                    className="w-full px-4 py-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] text-sm"
                    placeholder="请输入您的称呼"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1E1E1E] mb-2">留言内容</label>
                  <textarea
                    required
                    maxLength={500}
                    value={messageForm.content}
                    onChange={(e) => setMessageForm({...messageForm, content: e.target.value})}
                    className="w-full px-4 py-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] text-sm resize-none"
                    rows={4}
                    placeholder="写下您想说的话..."
                  />
                  <p className="text-xs text-[#6C6C6C] mt-1 text-right">{messageForm.content.length}/500</p>
                </div>
                
                {/* 装饰图片 */}
                <div className="flex justify-center py-2">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E8F4F0] to-[#2A7A62]/20 flex items-center justify-center">
                    <i className="ri-quill-pen-line text-3xl text-[#2A7A62]"></i>
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="w-full py-3 bg-[#2A7A62] text-white rounded-lg font-medium hover:bg-[#236854] transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* 复制成功弹窗 */}
      {showCopySuccessModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCopySuccessModal(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 bg-[#2A7A62] rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-[#1E1E1E] mb-2">复制成功</h3>
            <p className="text-[#6C6C6C] mb-6">链接已复制到剪贴板，快去分享给好友吧！</p>
            <button
              onClick={() => setShowCopySuccessModal(false)}
              className="px-8 py-3 bg-[#2A7A62] text-white rounded-lg font-medium hover:bg-[#236854] transition-colors cursor-pointer whitespace-nowrap"
            >
              知道了
            </button>
          </div>
        </div>
      )}

      {/* 微信分享成功弹窗 */}
      {showWechatSuccessModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowWechatSuccessModal(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 bg-[#07C160] rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-wechat-fill text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-[#1E1E1E] mb-2">正在跳转微信</h3>
            <p className="text-[#6C6C6C] mb-6">请在微信中完成分享到朋友圈</p>
            <button
              onClick={() => setShowWechatSuccessModal(false)}
              className="px-8 py-3 bg-[#07C160] text-white rounded-lg font-medium hover:bg-[#06A050] transition-colors cursor-pointer whitespace-nowrap"
            >
              知道了
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StoryDetailPage;
